<script>
import { mapGetters, mapState } from 'vuex';
import featureFlags from '@enums/feature-flags';
import WorkflowEntities from '@enums/workflow-entities';
import tabNames from '@enums/tab-names';
import promoPlannerContainerItem from './promo-planner-container-item.vue';
import navigation from '@/js/navigation';

const {
    explicitlyAllow,
    canReadPromotion,
    canEditScenario,
    canEditSubCampaign,
    enablePublicPrivateEntities,
} = featureFlags;

const icons = [
    {
        field: 'duplicate',
        featureToggle: explicitlyAllow,
        component: {
            name: 'icon-button',
            featureToggle: explicitlyAllow,
            showPropName: 'canCopySubCampaign',
            props: {
                dataIdE2e: 'copySubCampaignBtnId',
                icon: 'custom:duplicate',
                class: 'duplicate-icon',
            },
            events: {
                click: {
                    method: 'duplicateClicked',
                    params: [],
                },
            },
        },
    },
    {
        field: 'showWorkflowState',
        featureToggle: canEditScenario,
        component: {
            name: 'workflow-state',
            featureToggle: explicitlyAllow,
            showPropName: 'showWorkflowDebugIcon',
            props: {
                workflowEntity: WorkflowEntities.subCampaign,
                small: true,
            },
            contextualProps: {
                entityId: '_id',
            },
        },
    },
    {
        field: 'showScenarioIcon',
        featureToggle: canReadPromotion,
        component: {
            name: 'icon-button',
            featureToggle: canReadPromotion,
            props: {
                dataIdE2e: 'showScenariosBtnId',
                icon: 'custom:expand-sub-campaign',
            },
            events: {
                click: {
                    method: 'viewScenarios',
                    params: [],
                },
            },
        },
    },
];

export default {
    extends: promoPlannerContainerItem,
    props: ['campaign', 'subCampaign', 'parentStartDate', 'parentEndDate', 'isLocked'],
    emits: ['opened', 'duplicate-sub-campaign-clicked'],
    data() {
        return { icons };
    },
    computed: {
        ...mapState('subCampaigns', ['selectedSubCampaignId']),
        ...mapState('clientConfig', ['toggleLogic', 'generalConfig']),
        ...mapGetters('subCampaigns', ['selectedSubCampaign', 'getTotalExpectedPromotionsCounter']),
        ...mapGetters('campaigns', ['isCampaignSelected']),

        context() {
            return this.subCampaign;
        },

        selectedFadedClass() {
            // 1. check for selectedSubCampaign and
            // 2. if preparation tab (isReadOnly = true) =>
            // 2.1. check if current subCampaign id is the same selected one
            // 3. if planning tab (isReadOnly = false) =>
            // 3.1. check if current view is a single campaign view and user can edit scenario =>
            // 3.1. check for 2.1
            if (
                this.selectedSubCampaign &&
                (this.isReadOnly ||
                    (this.isCampaignSelected && !!this.toggleLogic[canEditScenario]))
            ) {
                if (this.selectedSubCampaign._id !== this.subCampaign._id) {
                    return 'sub-campaign--faded';
                }
                return 'sub-campaign--selected';
            }

            return '';
        },

        showWorkflowDebugIcon() {
            return this.isCampaignSelected && this.generalConfig.showWorkflowDebugIcon;
        },

        canCopySubCampaign() {
            return (
                !!this.generalConfig.allowSubCampaignCopying &&
                !!this.toggleLogic[canEditSubCampaign] &&
                !this.isCampaignConcluded
            );
        },

        isCampaignConcluded() {
            return (
                !!this.campaign &&
                this.$moment(this.campaign.endDate).isBefore(this.$moment(), 'day')
            );
        },

        isPrivate() {
            return (
                this.toggleLogic[enablePublicPrivateEntities] &&
                this.subCampaign &&
                this.subCampaign.isPrivate
            );
        },

        cssClasses() {
            return ['sub-campaign', this.selectedFadedClass];
        },

        totalExpectedPromotionsCounter() {
            return this.getTotalExpectedPromotionsCounter(this.subCampaign);
        },
    },
    methods: {
        onClick() {
            if (this.isReadOnly) {
                // show pagination
                navigation.toSubCampaignView({
                    tabName: tabNames.preparation,
                    subCampaignId: this.subCampaign._id,
                    router: this.$router,
                });
            } else {
                this.$emit('opened', { subCampaign: this.subCampaign });
            }
        },

        viewScenarios() {
            if (
                !this.selectedSubCampaignId ||
                this.selectedSubCampaignId !== this.subCampaign._id
            ) {
                navigation.toFavouriteScenario({
                    subCampaign: this.subCampaign,
                    router: this.$router,
                });
            }
        },

        duplicateClicked() {
            this.$emit('duplicate-sub-campaign-clicked', {
                subCampaign: this.subCampaign,
                campaign: this.campaign,
            });
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.sub-campaign {
    background-color: $promo-campaign-color-3;
    border-color: $promo-campaign-border-3;
    color: $promo-text-colour;
    &:hover {
        background-color: $promo-campaign-color-4;
        border-color: $promo-campaign-border-4;
        color: $promo-text-colour;
        cursor: pointer;

        .sub-campaign__scenario-btn :deep(svg path) {
            fill: $rtls-primary-colour;
        }
    }

    &--faded {
        background-color: $faded-promo-campaign-color-3;
        border-color: $faded-promo-campaign-border-3;
        color: $faded-promo-text-colour;
    }

    &#{&}--selected {
        .sub-campaign__scenario-btn :deep(svg path) {
            fill: $rtls-primary-colour;
        }
    }

    & &__scenario-btn :deep(svg path) {
        fill: $promo-grey-7;

        &:active {
            fill: $rtls-primary-button-hover-colour;
        }
    }
}
</style>
